/**
 * Database monitoring and error tracking utilities
 */

interface DatabaseError {
  timestamp: Date;
  error: string;
  query?: string;
  table?: string;
  context?: string;
}

class DatabaseMonitor {
  private errors: DatabaseError[] = [];
  private readonly maxErrors = 100;
  
  logError(error: string, context?: { query?: string; table?: string; context?: string }) {
    const dbError: DatabaseError = {
      timestamp: new Date(),
      error,
      ...context
    };
    
    this.errors.unshift(dbError);
    
    // Keep only recent errors
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }
    
    // Log critical errors immediately
    if (error.includes('Out of memory') || error.includes('connection')) {
      console.error('Critical database error:', dbError);
    }
  }
  
  getRecentErrors(minutes: number = 5): DatabaseError[] {
    const cutoff = new Date(Date.now() - minutes * 60 * 1000);
    return this.errors.filter(err => err.timestamp >= cutoff);
  }
  
  getErrorStats() {
    const last5min = this.getRecentErrors(5);
    const last1hour = this.getRecentErrors(60);
    
    return {
      last5Minutes: last5min.length,
      lastHour: last1hour.length,
      memoryErrors: this.errors.filter(e => e.error.includes('memory')).length,
      connectionErrors: this.errors.filter(e => e.error.includes('connection')).length,
      enumErrors: this.errors.filter(e => e.error.includes('enum')).length,
    };
  }
  
  clearErrors() {
    this.errors = [];
  }
}

export const databaseMonitor = new DatabaseMonitor();

// Enhanced error wrapper for database operations
export const withDatabaseErrorHandling = async <T>(
  operation: () => Promise<T>,
  context?: { table?: string; operation?: string }
): Promise<T | null> => {
  try {
    return await operation();
  } catch (error: any) {
    const errorMessage = error.message || 'Unknown database error';
    
    databaseMonitor.logError(errorMessage, {
      table: context?.table,
      context: context?.operation,
    });
    
    // For enum errors, provide helpful guidance
    if (errorMessage.includes('enum')) {
      console.warn('Enum error detected - this may require database migration');
    }
    
    // For memory errors, trigger cleanup
    if (errorMessage.includes('memory')) {
      console.warn('Memory error detected - consider reducing query size');
    }
    
    throw error; // Re-throw to maintain error handling flow
  }
};
